import { useContext, useState } from "react";
import { AuthContext } from "../context/authContext";
import axios from "axios";
import { baseUrl } from "../modules/account/login";
import { useNavigate } from "react-router";
import { keepPreviousData, useMutation, useQuery } from "@tanstack/react-query";
import { SetupIntent } from "@stripe/stripe-js";

export function usePaymentHistory() {
  const { token } = useContext(AuthContext);
  const AuthStr = "Bearer ".concat(token!);
  const navigate = useNavigate();

  const getData = () => {
    return axios
      .get(`${baseUrl}/payments/history`, {
        headers: { Authorization: AuthStr },
      })
      .then((response) => {
        const data = response.data;
        return data;
      })
      .catch((e) => {
        if (e?.response?.data?.message === "Session expired")
          navigate("/account/logout");
        return Promise.reject(
          new Error(e?.message ?? "Check your internet connection")
        );
      });
  };

  const { data, error, isLoading, refetch } = useQuery({
    queryKey: ["payment_history", token],
    queryFn: () => getData(),
    placeholderData: keepPreviousData,
    refetchOnWindowFocus: false,
  });

  return { data, error, isLoading, refetch };
}

export function useUserCards({ token }) {
  const AuthStr = "Bearer ".concat(token!);
  const navigate = useNavigate();

  const getData = () => {
    return axios
      .get(`${baseUrl}/payments/stripe/get_users_card`, {
        headers: { Authorization: AuthStr },
      })
      .then((response) => {
        const data = response.data;
        return data;
      })
      .catch((e) => {
        if (e?.response?.data?.message === "Session expired")
          navigate("/account/logout");
        return Promise.reject(
          new Error(e?.message ?? "Check your internet connection")
        );
      });
  };

  const { data, error, isLoading, refetch } = useQuery({
    queryKey: ["user_cards", token],
    queryFn: () => getData(),
    placeholderData: keepPreviousData,
    refetchOnWindowFocus: false,
    enabled: !!token,
  });

  return { data, error, isLoading, refetch };
}

export function useSaveCard() {
  const { token } = useContext(AuthContext);
  const navigate = useNavigate();

  return useMutation({
    mutationFn: async (setupIntent: SetupIntent) => {
      try {
        const response = await axios.post(
          `${baseUrl}/payments/stripe/save_card`,
          setupIntent,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
        return response.data;
      } catch (e: any) {
        if (e?.response?.data?.message === "Session expired") {
          navigate("/account/logout");
        }
        throw new Error(e?.message ?? "Check your internet connection");
      }
    },
  });
}

export function useFundWallet() {
  const { token } = useContext(AuthContext);
  const navigate = useNavigate();

  return useMutation({
    mutationFn: async (amount: number) => {
      try {
        const response = await axios.post(
          `${baseUrl}/payments/stripe/fund_wallet`,
          { amount },
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
        return response.data;
      } catch (e: any) {
        if (e?.response?.data?.message === "Session expired") {
          navigate("/account/logout");
        }
        return Promise.reject({
          message:
            e?.response?.data?.details?.message ??
            "Check your internet connection",
        });
      }
    },
  });
}
